import { useField } from "formik";
import { useState } from "react";

const Rule = ({ ok, text }) => (
  <div className={`flex items-center gap-2 ${ok ? "text-green-600" : "text-gray-400"}`}>
    <span>{ok ? "✔" : "✖"}</span>
    <span>{text}</span>
  </div>
);


const checkUsernameRules = (value = "") => ({
  isValid: /^[a-zA-Z0-9_]{1,50}$/.test(value),
  validLength: value.length >= 1 && value.length <= 50,
  onlyValidChars: /^[a-zA-Z0-9_]*$/.test(value),
});


const checkPasswordRules = (value = "") => ({
  minLength: value.length >= 8,
  hasUppercase: /[A-Z]/.test(value),
  hasLowercase: /[a-z]/.test(value),
  hasNumber: /[0-9]/.test(value),
  hasSpecialChar: /[@#$%^&+=]/.test(value),
});



const TextInput = ({
  label,
  labelClassName,
  endAdornment,
  errorMessage,
  successMessage,
  customError,
  required = false,
  placeholder,
  onBlur,
  showUsernameRules = false,
  showPasswordRules = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [field, meta] = useField(props);

  const value = field.value || "";
  const rules = showUsernameRules ? checkUsernameRules(value) : checkPasswordRules(value);

  // Determine if there's any error
  const hasError = meta.touched && (meta.error || errorMessage || customError);

  // Check if the error is specifically "Required"
  const isRequiredError = hasError &&
    (meta.error === "Required" ||
      errorMessage === "Required" ||
      customError === "Required");


  const showAsterisk = required && hasError;

  // Show error message below ONLY when it's NOT a simple "Required" error
  const showErrorMessage = hasError && !isRequiredError;

  const errorText = meta.error || errorMessage || customError || "";

  const handleBlur = (e) => {
    field.onBlur(e);
    if (onBlur) onBlur(e);
  };

  return (
    <div className="flex flex-col">
      {/* Label with conditional red asterisk */}
      <label className={`p-1 text-gray-700 flex items-center gap-1 ${labelClassName || 'text-lg'}`}>
        {label}
        {showAsterisk && <span className="text-red-500 text-base font-bold">*</span>}
      </label>

      <div
        className={`relative rounded-lg border border-gray-300`}
      >
        <input
          {...field}
          {...props}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            handleBlur(e);
          }}
          className="p-2 bg-transparent outline-none w-full placeholder-gray-400 placeholder:text-[14px]"
          placeholder={placeholder || ""}
        />

        {endAdornment && (
          <div className="absolute inset-y-0 right-0 pl-2 pr-2 flex items-center border border-[rgba(222,226,230,1)]">
            {endAdornment}
          </div>
        )}

        {showUsernameRules && isFocused && value.length > 0 && (
          <div className="absolute z-50 top-full mt-1 w-[280px] 
                  p-2 rounded-md bg-white border shadow-lg text-xs space-y-1">
            <Rule ok={rules.onlyValidChars} text="Only letters, numbers, underscore (_)" />
            <Rule ok={rules.validLength} text="1–50 characters long" />
          </div>
        )}


        {showPasswordRules && isFocused && value.length > 0 && (
          <div
            className="absolute z-50 top-full left-0 mt-1 w-[320px]
               p-2 rounded-md bg-white border shadow-lg text-xs space-y-1"
          >
            <Rule ok={rules.minLength} text="At least 8 characters" />
            <Rule ok={rules.hasUppercase} text="At least one uppercase letter (A–Z)" />
            <Rule ok={rules.hasLowercase} text="At least one lowercase letter (a–z)" />
            <Rule ok={rules.hasNumber} text="At least one number (0–9)" />
            <Rule ok={rules.hasSpecialChar} text="At least one special character (@#$%^&+=)" />
          </div>
        )}

      </div>



      {/* Show error message ONLY for non-required errors */}
      {showErrorMessage && (
        <div className="text-red-500 text-[12px] mt-1">
          {errorText}
        </div>
      )}

      {/* Success message (unchanged) */}
      {meta.touched && !hasError && successMessage && (
        <div className="text-green-600 text-[12px] mt-1">
          {successMessage}
        </div>
      )}
    </div>
  );
};

export default TextInput;